import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const formatCurrency = (value) => {
  return `$${value.toFixed(2)}`;
};

const formatDate = (dateStr) => {
  // Parse YYYY-MM-DD directly to avoid timezone conversion
  const [year, month, day] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
};

// Process data to group by date and stack by request type
const processData = (rawData) => {
  const dataByDate = {};
  const requestTypes = new Set();

  rawData.forEach(item => {
    const { date, requestType, totalCost } = item;
    if (!dataByDate[date]) {
      dataByDate[date] = { date };
    }
    dataByDate[date][requestType] = totalCost;
    requestTypes.add(requestType);
  });

  return {
    data: Object.values(dataByDate),
    requestTypes: Array.from(requestTypes)
  };
};

function LlmCostByTypeChart({ data }) {
  const { data: processedData, requestTypes } = processData(data);
  
  // Define colors for different types
  const blueShades = ['#0066cc', '#3385ff', '#66a3ff', '#99c2ff', '#cce0ff'];
  const defaultColors = ['#82ca9d', '#ffc658', '#a4de6c', '#d0ed57'];
  const panelColor = '#ff8042'; // Orange for PANEL type
  
  // Function to determine if a type is chat-related
  const isChatType = (type) => type.startsWith('CHAT');
  
  // Sort request types to ensure PANEL is at the bottom of the stack
  const sortedTypes = [...requestTypes].sort((a, b) => {
    if (a === 'PANEL') return -1;
    if (b === 'PANEL') return 1;
    return 0;
  });
  
  // Assign colors based on type
  const getColor = (type, index) => {
    if (type === 'PANEL') return panelColor;
    if (isChatType(type)) {
      return blueShades[index % blueShades.length];
    }
    return defaultColors[index % defaultColors.length];
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="date" 
          tickFormatter={formatDate}
        />
        <YAxis 
          tickFormatter={formatCurrency}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              return (
                <div style={{ 
                  backgroundColor: '#fff',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}>
                  <p style={{ margin: '0', fontWeight: 'bold' }}>{formatDate(label)}</p>
                  {payload.map((entry, index) => (
                    <p key={index} style={{ margin: '5px 0', color: entry.color }}>
                      {entry.name}: {formatCurrency(entry.value)}
                    </p>
                  ))}
                  <p style={{ margin: '5px 0', fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '5px' }}>
                    Total: {formatCurrency(payload.reduce((sum, entry) => sum + entry.value, 0))}
                  </p>
                </div>
              );
            }
            return null;
          }}
        />
        <Legend />
        {sortedTypes.map((type, index) => (
          <Bar 
            key={type}
            dataKey={type} 
            stackId="costs" 
            fill={getColor(type, index)} 
            name={type}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default LlmCostByTypeChart;
